/* global FavoritesEnabled */
import React, { Component } from 'react';
import { Link, withRouter } from 'react-router';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Badge from 'components/Badge';
import Icon from 'components/Icon';
import Dropdown from 'components/Dropdown';
import Favorite, { FAVORITE_TYPE_ICON } from 'components/Favorite';
import { requiresAuthElement } from 'helpers/VideoProcessorHelper';
import { PageNames, SegmentSources, tealiumEventPageTypes } from '@foxcorp/delta-web-analytics';
import { now } from 'helpers/DateTimeHelper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import { OVERLAY_LIVE_CTA_OVERFLOW } from 'helpers/FilterHelper';
import { getVideoData, getMobileVideoMetadata, getVideoTimeData } from 'screens/Live/LiveHelpers';

import favoriteTheme from 'screens/Live/styles/Favorite.css';
import dropdownTheme from 'screens/Live/styles/Dropdown.css';
import Style from './PrimeTimeItem.module.scss';

const pageType = _.get(tealiumEventPageTypes, PageNames.LIVE_SCHEDULE);

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    enableOverflowDropdown: false,
  },
  [SITE_PLATFORM_WEB]: {
    enableOverflowDropdown: false,
  },
  [SITE_PLATFORM_TABLET]: {
    enableOverflowDropdown: true,
  },
  [SITE_PLATFORM_MOBILE]: {
    enableOverflowDropdown: true,
  },
};

class PrimeTimeItem extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.toggleOverflowDropdown = this.toggleOverflowDropdown.bind(this);
  }

  toggleOverflowDropdown() {
    const { resetStillWatchingTimeout, setOverlay } = this.props;
    setOverlay(OVERLAY_LIVE_CTA_OVERFLOW);
    resetStillWatchingTimeout();
  }

  renderCTAButtons(video, channelIdx) {
    if (!video) return null;

    const {
      fullEpisodeCount,
      id,
      moreInfoUrl,
      requiresAuthLive,
      shouldRenderLiveCta,
      shouldRenderVODCta,
      watchUrl,
    } = getVideoData(video);
    const { isCurrent, isPast } = getVideoTimeData({ video });
    const { isAuthenticated, breakpoint, watchLiveClick, restartClick } = this.props;
    const liveBtnClasses = Style.actionButton;
    const { enableOverflowDropdown } = BREAKPOINTS[breakpoint];
    const {
      global_watchLiveButton: labelWatchLive,
      global_restartButton: labelRestart,
      global_moreSeriesDeeplinkButton: labelMoreEpisodes,
    } = window.Language;

    const canRestart = shouldRenderVODCta && (isCurrent || isPast);
    const lockIcon = requiresAuthElement({
      requiresAuth: requiresAuthLive,
      lockClass: Style.lock,
      isAuthenticated,
      viewBox: '0 4 24 20',
    });
    const contentLocked = requiresAuthLive && !isAuthenticated ? Style.contentLocked : null;
    const actionButtons = [];
    const ctaDetails = [];

    if (canRestart) {
      actionButtons.push(
        <Link
          key={`restart-button-${id}`}
          to={watchUrl}
          className={classNames(liveBtnClasses, contentLocked)}
          onClick={() => {
            restartClick(video, channelIdx);
          }}
        >
          <span>
            {lockIcon} {labelRestart}
          </span>
        </Link>,
      );
      ctaDetails.push({
        name: labelRestart,
        onClick: () => {
          restartClick(video, channelIdx);
        },
      });
    }

    if (shouldRenderLiveCta) {
      actionButtons.push(
        <Link
          key={`watch-live-${id}`}
          className={classNames(liveBtnClasses, contentLocked)}
          onClick={() => {
            watchLiveClick(video, channelIdx);
          }}
        >
          <span>
            {lockIcon} {labelWatchLive}
          </span>
        </Link>,
      );
      ctaDetails.push({
        name: labelWatchLive,
        onClick: () => {
          watchLiveClick(video, channelIdx);
        },
      });
    }

    if (fullEpisodeCount) {
      actionButtons.push(
        <Link key={`more-episodes-${id}`} className={liveBtnClasses} to={moreInfoUrl}>
          {labelMoreEpisodes}
        </Link>,
      );
      ctaDetails.push({
        name: labelMoreEpisodes,
        onClick: () => {
          const { router } = this.props;
          router.push(moreInfoUrl);
        },
      });
    }

    const mobileActionButtons =
      actionButtons.length > 2 ? actionButtons.slice(0, 2) : actionButtons;

    return (
      <div
        className={Style.actionButtons}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        {enableOverflowDropdown ? mobileActionButtons : actionButtons}
        {enableOverflowDropdown && this.renderOverflowDropdown(ctaDetails)}
      </div>
    );
  }

  renderDetails({ subtitle, description, title }) {
    const { item } = this.props;
    const metadata = getMobileVideoMetadata(item);
    const actionsButtonsElement = this.renderCTAButtons(item, 0);
    const metadataElement = <div className={Style.detailInfo}>{metadata}</div>;

    return (
      <div className={Style.detailContainerLeft}>
        <h2 className={Style.detailTitle}>
          {title} {this.renderFavorite()}
        </h2>
        <div className={Style.detailSubtitle}>{subtitle}</div>
        <div className={Style.detailDescription}>{description}</div>
        {metadataElement}
        {actionsButtonsElement}
      </div>
    );
  }

  renderFavorite() {
    const { item, favoritesManager } = this.props;
    const { showCode: showID, network, seriesName, favoritableItems } = item;
    const recommendationId = _.get(item, 'trackingData.properties.recommendationId', null);

    const favoriteData = {
      showID,
      networkId: network,
      seriesName,
      source: SegmentSources.LIVE,
      location: pageType,
      recommendationId,
      favoritableItems,
    };

    if (!FavoritesEnabled) return null;

    return (
      <div
        role="button"
        className={favoriteTheme.wrapper}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <Favorite
          manager={favoritesManager}
          data={favoriteData}
          theme={favoriteTheme}
          type={FAVORITE_TYPE_ICON}
        />
      </div>
    );
  }

  renderItem() {
    const { item, idx, openIdx, date, openIdxDate } = this.props;
    const isOpen = openIdx === idx && openIdxDate === date;

    const { starts } = getVideoTimeData({ video: item, current: now() });
    const { title, subtitle, description, badge } = getVideoData(item);
    const arrow = (
      <Icon color="white" icon={isOpen ? 'keyboard-arrow-up' : 'keyboard-arrow-down'} />
    );
    const badgeElement = badge ? <Badge theme={Style} badge={badge} /> : null;

    let detailsElement = (
      <span className={Style.titleContainer}>
        <span className={Style.title}>
          {badgeElement}
          <span className={Style.titleText}>{title}</span>
        </span>
        <span className={Style.subtitle}>{subtitle}</span>
      </span>
    );
    if (isOpen) {
      detailsElement = this.renderDetails({ subtitle, description, title });
    }

    return (
      <div className={Style.container}>
        <span className={Style.timestamp}>{starts}</span>
        {detailsElement}
        <span className={Style.caret}>{arrow}</span>
      </div>
    );
  }

  renderOverflowDropdown(ctaDetails) {
    if (ctaDetails.length < 3) return null;
    const { overlay } = this.props;
    const isOpen = overlay === OVERLAY_LIVE_CTA_OVERFLOW;
    const overflowHoverEffect = isOpen ? null : dropdownTheme.overflowHoverEffect;
    const items = ctaDetails.slice(2);

    return (
      <div
        className={Style.overflowButtonWrapper}
        onClick={() => {
          this.toggleOverflowDropdown();
        }}
      >
        <Dropdown
          theme={dropdownTheme}
          items={items}
          onOpenDropdown={this.toggleOverflowDropdown}
          onCloseDropdown={this.toggleOverflowDropdown}
          displayResults
          showMobileCancel
          expanded={isOpen}
          className={classNames(
            Style.dropdown,
            dropdownTheme.overflowDropdown,
            overflowHoverEffect,
          )}
          getIcon={() => {}}
          defaultText="• • •"
        />
      </div>
    );
  }

  render() {
    const { item, idx, date, overlay, toggleItem } = this.props;
    const { title } = getVideoData(item);
    const { starts, ends } = getVideoTimeData({ video: item, current: now() });
    const hoverTitle = `${title}\n(${starts} - ${ends})`;
    const itemZIndex = overlay === OVERLAY_LIVE_CTA_OVERFLOW ? Style.open : Style.closed;

    return (
      <div
        className={classNames(Style.primeTimeMain, itemZIndex)}
        title={hoverTitle}
        onClick={() => {
          toggleItem(idx, date);
        }}
      >
        {this.renderItem()}
      </div>
    );
  }
}

PrimeTimeItem.defaultProps = {
  breakpoint: null,
  favoritesManager: null,
  isAuthenticated: false,
  openIdx: null,
  openIdxDate: null,
  overlay: null,
  resetStillWatchingTimeout: () => {},
  router: {},
  toggleItem: () => {},
};

PrimeTimeItem.propTypes = {
  breakpoint: PropTypes.string,
  date: PropTypes.string.isRequired,
  favoritesManager: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    setFavorites: PropTypes.func.isRequired,
    deleteFavorites: PropTypes.func.isRequired,
  }),
  idx: PropTypes.number.isRequired,
  isAuthenticated: PropTypes.bool,
  item: PropTypes.shape({}).isRequired,
  openIdx: PropTypes.number,
  openIdxDate: PropTypes.string,
  overlay: PropTypes.string,
  toggleItem: PropTypes.func,
  resetStillWatchingTimeout: PropTypes.func,
  restartClick: PropTypes.func.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
  setOverlay: PropTypes.func.isRequired,
  watchLiveClick: PropTypes.func.isRequired,
};

export default withRouter(PrimeTimeItem);
